import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { authApi } from '@services/authApi'
import './PortalLoginPage.css'

type VerifyState = 'verifying' | 'success' | 'error' | 'missing'

/**
 * Email-verification page (KAN-782). Reads the single-use token from `?token=` (the emailed link)
 * and posts it to `POST /api/v2/auth/verify-email`, then shows the outcome. Unknown/expired/used
 * tokens are reported with one generic message (the backend rejects them uniformly).
 */
export function VerifyEmailPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const [state, setState] = useState<VerifyState>(token ? 'verifying' : 'missing')
  // Guard against the StrictMode double-invoke firing the single-use redeem twice.
  const startedRef = useRef(false)

  useEffect(() => {
    if (!token || startedRef.current) return
    startedRef.current = true
    authApi
      .verifyEmail(token)
      .then(() => setState('success'))
      .catch(() => setState('error'))
  }, [token])

  const heading =
    state === 'success'
      ? 'Email verified'
      : state === 'verifying'
        ? 'Verifying your email…'
        : 'Verification failed'

  return (
    <div className="portal-login portal-login-customer">
      <div className="portal-login__layout">
        <div className="portal-login__card">
          <header className="portal-login__header">
            <span className="portal-login__badge">VERIFY EMAIL</span>
            <h1>{heading}</h1>
          </header>

          <div className="portal-login__form">
            {state === 'verifying' && (
              <p className="portal-login__otp-hint">Please wait while we confirm your email address.</p>
            )}
            {state === 'success' && (
              <p className="portal-login__otp-hint">
                Your email address has been verified. You're all set.
              </p>
            )}
            {(state === 'error' || state === 'missing') && (
              <div className="portal-login__error">
                This verification link is invalid or has expired. Please sign in and request a new
                verification email.
              </div>
            )}

            <button
              type="button"
              className="portal-login__submit"
              onClick={() => navigate('/login')}
            >
              Continue to Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VerifyEmailPage
